import {
	ChatBubbleOvalLeftEllipsisIcon,
	EllipsisHorizontalCircleIcon,
	HandThumbDownIcon,
	HandThumbUpIcon,
	TrashIcon,
} from "@heroicons/react/24/outline";
import { Link, useRouteContext } from "@tanstack/react-router";
import { EditorContent } from "@tiptap/react";
import {
	differenceInDays,
	differenceInHours,
	differenceInMinutes,
	differenceInMonths,
} from "date-fns";
import { motion } from "motion/react";
import { useMemo, useState, useTransition } from "react";
import {
	ConnectionHandler,
	graphql,
	useFragment,
	useMutation,
	usePaginationFragment,
} from "react-relay";
import { Icon } from "@/components/icon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { useMobileReply } from "@/contexts/mobile-reply-context";
import { JoinModal } from "@/dialogs/join-dialog";
import { useEditor } from "@/editor/editor";
import { cn } from "@/utils";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";
import type { childrenCommentsQuery } from "./__generated__/childrenCommentsQuery.graphql";
import type { comment_childrenComments$key } from "./__generated__/comment_childrenComments.graphql";
import type { comment_parentComment$key } from "./__generated__/comment_parentComment.graphql";
import type { commentCreateChildrenMutation } from "./__generated__/commentCreateChildrenMutation.graphql";
import type { commentDeleteMutation } from "./__generated__/commentDeleteMutation.graphql";
import type { commentFragment$key } from "./__generated__/commentFragment.graphql";
import type { commentVoteMutation } from "./__generated__/commentVoteMutation.graphql";
import { CommentComposer } from "./comment-composer";

export function Comment({
	comment,
	refetch,
}: {
	comment: commentFragment$key;
	refetch?: () => void;
}) {
	const { club, session } = useRouteContext({ from: "__root__" });
	const { track } = useAnalytics();
	const { replyTarget, setReplyTarget } = useMobileReply();
	const [_, startTransition] = useTransition();
	const [hoveringName, setHoveringName] = useState(false);
	const [composerOpen, setComposerOpen] = useState(false);
	const [joinModalOpen, setJoinModalOpen] = useState(false);

	const data = useFragment<commentFragment$key>(
		graphql`
    fragment commentFragment on CommentInterface {
      id
      content
      author {
				id
        avatarFallback
        displayName
        photo {
          signedUrl
        }
        username
      }
      parent {
        id
      }
      votes
      userVote {
        vote
      }
      childrenCount
      createdAt

      __typename
    }
  `,
		comment,
	);

	const [addComment, isPending] =
		useMutation<commentCreateChildrenMutation>(graphql`
	  mutation commentCreateChildrenMutation($payload: CreateCommentInput!, $connections: [ID!]!) {
			  registerComment(payload: $payload) {
					commentEdge @prependEdge(connections: $connections) {
					  node {
              id
              ...commentFragment
            }
            cursor
          }
        }
			}
	`);

	const editor = useEditor({
		editable: false,
		content: data.content,
		size: "sm",
		placeholder: "Escreva sua postagem aqui...",
		placeholderType: "first-line",
		session,
		club: club.slug,
		mimeTypes: ["*"],
		features: {
			button: { enabled: true },
			mention: { enabled: true },
			files: { enabled: false },
			image: { enabled: true },
		},
	});

	const [vote] = useMutation<commentVoteMutation>(graphql`
    mutation commentVoteMutation($commentId: ID!, $vote: PostVoteType!) {
        registerCommentVote(commentId: $commentId, vote: $vote) {
            id
            ...commentFragment
        }
    }
	`);

	const [deleteComment] = useMutation<commentDeleteMutation>(graphql`
		mutation commentDeleteMutation($commentId: ID!) {
			deleteComment(id: $commentId) {
				id @deleteRecord
			}
		}
	`);

	const canDelete =
		Boolean(data.author?.id && data.author.id === session?.user?.id) ||
		(club.permissions?.length || 0) > 0;

	const distance = useMemo(() => {
		const currentDate = new Date();
		const commentDate = new Date(data.createdAt);

		const minutes = differenceInMinutes(currentDate, commentDate);

		if (minutes < 5) {
			return "alguns minutos";
		} else if (minutes < 60) {
			return `${minutes} minutos`;
		}

		const hours = differenceInHours(currentDate, commentDate);

		if (hours === 1) {
			return "1 hora";
		} else if (hours < 24) {
			return `${hours} horas`;
		}

		const days = differenceInDays(currentDate, commentDate);

		if (days === 1) {
			return "1 dia";
		} else if (days < 30) {
			return `${days} dias`;
		}

		const months = differenceInMonths(currentDate, commentDate);

		if (months < 1) {
			return days === 1 ? "1 dia" : `${days} dias`;
		}

		if (months === 1) {
			return "1 mês";
		}

		return `${months} meses`;
	}, [data.createdAt]);

	const connections = [
		"BEST",
		"LEAST_UPVOTED",
		"MOST_UPVOTED",
		"NEWEST",
		"OLDEST",
		"TRENDING",
	].map((order) =>
		ConnectionHandler.getConnectionID(
			data.parent?.id || data.id,
			"childrenComments__comments",
			{
				orderBy: order,
			},
		),
	);

	const isReplyTarget = replyTarget?.commentId === data.id;

	return (
		<Dialog open={joinModalOpen} onOpenChange={setJoinModalOpen}>
			<div
				className={cn(
					"w-full transition-all duration-200 rounded",
					isReplyTarget && "border-l-3 border-primary! bg-primary/5 p-2",
				)}
			>
				{data.parent && <Separator className="mb-6" />}

				<div className="w-full flex flex-col gap-3 overflow-hidden wrap-break-word">
					<div className="flex flex-row items-center justify-between w-full">
						<div className="flex flex-row items-center justify-start gap-3">
							<Avatar>
								<AvatarFallback>{data.author?.avatarFallback}</AvatarFallback>
								<AvatarImage src={data.author?.photo?.signedUrl} />
							</Avatar>

							<Link
								to="/u/$username"
								params={{ username: data.author?.username! }}
								className="text-base/6 text-foreground font-medium"
								onMouseEnter={() => setHoveringName(true)}
								onMouseLeave={() => setHoveringName(false)}
							>
								<motion.span
									key="author-title"
									className={cn(
										"flex flex-row items-center justify-start gap-2 rounded",
										hoveringName &&
											"bg-muted text-muted-foreground pr-2 transition-all",
									)}
									style={{
										paddingLeft: hoveringName ? 8 : 0,
										paddingRight: hoveringName ? 8 : 0,
									}}
								>
									{data.author?.displayName}

									{hoveringName && (
										<motion.div
											key="author-icon"
											initial={{ opacity: 0, translateX: -5 }}
											animate={{ opacity: 1, translateX: 0 }}
											exit={{ opacity: 0, translateX: -5 }}
										>
											<Icon
												name="MoveUpRight"
												className="size-3 text-muted-foreground"
											/>
										</motion.div>
									)}
								</motion.span>
							</Link>
						</div>

						<span className="text-sm/5 text-card-foreground max-lg:hidden">
							Publicado há {distance}
						</span>
						<span className="text-sm/5 text-card-foreground lg:hidden">
							Há {distance}
						</span>
					</div>

					<div className="w-full pl-11 wrap-break-word min-w-0 overflow-hidden">
						<EditorContent editor={editor} />
					</div>

					<div className="flex flex-row items-center ml-9">
						<div className="flex flex-row items-center justify-center w-fit gap-0">
							<Button
								type="button"
								size="icon"
								variant="ghost"
								className={cn(
									data.userVote?.vote === "UP" &&
										"text-green-600 dark:text-green-500",
								)}
								onClick={() => {
									if (!session?.user) {
										setJoinModalOpen(true);
										return;
									}

									vote({ variables: { commentId: data.id, vote: "UP" } });
									track(AnalyticsEvents.COMMENT_VOTED, {
										comment_id: data.id,
										vote: "up",
									});
								}}
							>
								<HandThumbUpIcon className="size-5" />
							</Button>

							<div className="text-center text-base/6 w-fit px-2 foreground font-medium">
								{data.votes}
							</div>

							<Button
								type="button"
								size="icon"
								variant="ghost"
								className={cn(
									data.userVote?.vote === "DOWN" && "text-destructive",
								)}
								onClick={() => {
									if (!session?.user) {
										setJoinModalOpen(true);
										return;
									}

									vote({ variables: { commentId: data.id, vote: "DOWN" } });
									track(AnalyticsEvents.COMMENT_VOTED, {
										comment_id: data.id,
										vote: "down",
									});
								}}
							>
								<HandThumbDownIcon className="size-5" />
							</Button>
						</div>

						{!!data.author && (
							<Button
								type="button"
								variant="ghost"
								onClick={() => {
									const isMobile =
										window.matchMedia("(max-width: 767px)").matches;
									if (isMobile) {
										setReplyTarget({
											commentId: data.id,
											parentCommentId: data.parent?.id || data.id,
											authorUsername: data.author?.username ?? "",
											authorDisplayName: data.author?.displayName ?? "",
											content: data.content,
											connections,
											refetch: () => refetch?.(),
										});
									} else {
										setComposerOpen((s) => !s);
									}
								}}
								className="ml-6"
							>
								<ChatBubbleOvalLeftEllipsisIcon className="size-5" />
								Responder
							</Button>
						)}

						{canDelete ? (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										type="button"
										size="icon"
										variant="ghost"
										aria-label="Opções do comentário"
									>
										<EllipsisHorizontalCircleIcon className="size-5" />
									</Button>
								</DropdownMenuTrigger>

								<DropdownMenuContent>
									<DropdownMenuItem
										variant="destructive"
										onClick={() => {
											if (
												confirm(
													"Tem certeza que deseja deletar este comentário? Essa ação não poderá ser desfeita.",
												)
											) {
												track(AnalyticsEvents.COMMENT_DELETED, {
													comment_id: data.id,
												});
												deleteComment({ variables: { commentId: data.id } });
											}
										}}
									>
										<TrashIcon />
										Deletar Comentário
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						) : null}
					</div>
				</div>

				{Boolean(composerOpen && data.author) && (
					<div className="mt-3 max-md:hidden">
						<CommentComposer
							isLoading={isPending}
							initialContent={
								data?.parent
									? {
											type: "doc",
											content: [
												{
													type: "paragraph",
													attrs: {
														textAlign: "left",
													},
													content: [
														{
															type: "mention",
															attrs: {
																id: data.author?.username,
																label: null,
															},
														},
													],
												},
											],
										}
									: undefined
							}
							onSubmit={(content) =>
								new Promise((resolve, reject) => {
									addComment({
										variables: {
											payload: {
												parent: data.parent?.id || data.id,
												content: JSON.stringify(content),
											},
											connections,
										},
										updater: (store) => {
											connections.map((c) => store.get(c)?.invalidateRecord());
										},
										onCompleted: (response) => {
											track(AnalyticsEvents.COMMENT_REPLY_CREATED, {
												parent_comment_id: data.parent?.id || data.id,
											});

											startTransition(() => {
												refetch?.();
											});

											setComposerOpen(false);
											resolve(response);
										},
										onError: reject,
									});
								})
							}
							hideAvatar
						/>
					</div>
				)}

				<JoinModal />
			</div>
		</Dialog>
	);
}

export function ParentComment({
	comment,
}: {
	comment: comment_parentComment$key;
}) {
	const data = useFragment<comment_parentComment$key>(
		graphql`
    fragment comment_parentComment on CommentInterface {
      ...commentFragment
      ...comment_childrenComments
      childrenCount
    }
  `,
		comment,
	);
	const {
		data: childrenComments,
		refetch,
		hasNext,
		loadNext,
	} = usePaginationFragment<
		childrenCommentsQuery,
		comment_childrenComments$key
	>(
		graphql`
    fragment comment_childrenComments on CommentInterface
    @refetchable(queryName: "childrenCommentsQuery")
    @argumentDefinitions(
        first: { type: "Int", defaultValue: 3 },
       	cursor: { type: "String" }
    )
    {
      comments(first: $first, after: $cursor, orderBy: OLDEST) @connection(key: "childrenComments__comments") {
        edges {
          node {
            ...commentFragment
          }
          cursor
        }
      }
    }
  `,
		data,
	);

	return (
		<div className="flex flex-col w-full">
			<Comment
				comment={data}
				refetch={() => {
					if (!childrenComments.comments.edges.length) {
						refetch({ first: 3 });
					}
				}}
			/>

			{childrenComments.comments?.edges?.length ? (
				<div className="flex flex-col gap-6 mt-6">
					{childrenComments.comments.edges
						.filter(Boolean)
						.map(({ node, cursor }) => (
							<div key={cursor} className="flex flex-row gap-3">
								<div className="size-8" />

								<Comment comment={node} refetch={() => refetch({ first: 3 })} />
							</div>
						))}

					{hasNext && (
						<Button
							variant="outline"
							className="w-fit mt-3"
							onClick={() => loadNext(3)}
						>
							Ver Mais
						</Button>
					)}
				</div>
			) : null}
		</div>
	);
}
